import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import Label from '@/components/basicComponents/label';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { CREATE_INVESTMENT_MUTATION, GET_CURRENT_MANAGERS_QUERY } from '@/components/dueDiligence-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { sortUniqueNoneEmpty } from '../modify/utils';

enum AssignManagerOptionEnum {
  CreateNewManager = 'Create a new manager',
  AssignToExisting = 'Assign to existing'
}

const assignManagerRadioButtonOptions = Object.values(AssignManagerOptionEnum);

interface IForm {
  name: string;
  managerOption: string;
  existingManager: string;
  newManagerName: string;
}

const defaultFormValues: IForm = {
  name: '',
  managerOption: AssignManagerOptionEnum.CreateNewManager,
  existingManager: '',
  newManagerName: ''
};

interface AddNewStrategyProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddNewStrategy = ({ isOpen, onClose }: AddNewStrategyProps) => {
  const navigate = useNavigate();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, handleSubmit, watch, setValue } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [existingManagerSelectOptions, setExistingManagerSelectOptions] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createInvestment, { loading: createInvestmentLoading }] = useMutation(CREATE_INVESTMENT_MUTATION);

  const { loading: existingManagersLoading } = useQuery(GET_CURRENT_MANAGERS_QUERY, {
    onCompleted: (data) => {
      const sortedManagerList = sortUniqueNoneEmpty([...data.getCurrentManagers.values]?.map((manager) => createSimpleSelectOption(manager)));
      setExistingManagerSelectOptions(sortedManagerList);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const getManagerId = (data: IForm) => {
    const { name, managerOption, newManagerName, existingManager } = data;
    if (managerOption === AssignManagerOptionEnum.CreateNewManager) {
      return newManagerName || name;
    }

    return existingManager;
  };

  const handleAddNewStrategy = async (data: IForm) => {
    try {
      setErrorMessage(null);
      const managerId = getManagerId(data);
      const { data: createInvestmentData } = await createInvestment({ variables: { data: { name: data.name, managerId } } });
      const { id } = createInvestmentData.createInvestment;
      navigate(`/strategyDetails/${id}?backToTitle=Manager Pipeline`);
      navigate(
        `/dealPipeline/modify?investment=${encodeURIComponent(id)}&backToTitle=Strategy Details&isNewStrategy=true`
      );
      onClose();
    } catch {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Strategy"
      confirmButton={{
        name: 'Confirm',
        onClick: handleSubmit(handleAddNewStrategy),
        disabled: createInvestmentLoading
      }}
      showCancelButton={!createInvestmentLoading}
      showCloseIcon={!createInvestmentLoading}
      isUseOutsideClick={!createInvestmentLoading}
    >
      {createInvestmentLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalBody>
            <Banner
              icon={<InfoIcon fill={themeVar('menu.tab')} />}
              title=""
              description="You will start with a blank form. You can enter details manually or upload documents later for help from the AI."
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Name is required'
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Name"
                  value={value}
                  onChange={(e) => {
                    const strategyName = e.target.value;
                    onChange(strategyName);
                    if (!strategyName) {
                      setValue('newManagerName', '');
                    }
                  }}
                  errorText={error?.message}
                  isRequiredField
                  autoFocus
                  size="md"
                />
              )}
            />
            <ManagerSectionWrapper>
              <div>
                <Label size="md">Manager</Label>
                <Controller
                  name="managerOption"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <OptionsWrapper>
                      {assignManagerRadioButtonOptions.map((option, index) => (
                        <RadioButton
                          key={option + index}
                          name="managerRadioButton"
                          radioButtonText={option}
                          selectedOption={value}
                          handleRadioChange={(event) => onChange(event.target.value)}
                        />
                      ))}
                    </OptionsWrapper>
                  )}
                />
              </div>
              <AnimatePresence mode="wait">
                {watch('managerOption') === AssignManagerOptionEnum.CreateNewManager && (
                  <motion.div key="newManagerNameInput" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Controller
                      name="newManagerName"
                      control={control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          value={value || ''}
                          onChange={onChange}
                          errorText={error?.message}
                          placeholder={watch('name') || ''}
                          onClick={() => {
                            const strategyName = watch('name');
                            const newManagerName = watch('newManagerName');
                            if (!newManagerName) {
                              onChange(strategyName || '');
                            }
                          }}
                          label="Manager Name (optional)"
                          size="md"
                        />
                      )}
                    />
                  </motion.div>
                )}
                {watch('managerOption') === AssignManagerOptionEnum.AssignToExisting && (
                  <motion.div key="existingManagerSelect" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Controller
                      name="existingManager"
                      control={control}
                      rules={{
                        required: {
                          value: true,
                          message: 'Manager is required'
                        }
                      }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          value={value}
                          setValue={(value) => {
                            onChange(value);
                            if (watch('newManagerName')) {
                              setValue('newManagerName', null);
                            }
                          }}
                          options={existingManagerSelectOptions}
                          errorText={error?.message}
                          label="Manager"
                          size="md"
                          loading={existingManagersLoading}
                          withSearch
                          isRequiredField
                        />
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </ManagerSectionWrapper>
          </ModalBody>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default AddNewStrategy;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const ManagerSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionsWrapper = styled.div`
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;
